import type { Game, Item, Tile } from "..";
import type { instanceId, itemId, mergeCause, Position, PositionF, tileKey } from "../../data/types";
import { centroidOf, unique } from "../../lib/algo";
import { calculateMergeDrops } from "../../lib/merge-helpers";
import { System, type EventHandlers } from "../../lib/core";

export type MergeEvents = {
  execute: { connectedItems: Item[]; spawn: { itemId: itemId; tile: Tile }[] };
  spawn: {
    connectedItems: Item[];
    spawn: { itemId: itemId; tile: Tile }[];
    center: Position;
  };
  complete: { spawnedItems: Item[] };
};

export class MergeSystem
  extends System<Game>()<MergeEvents>(["execute", "spawn", "complete"])
  implements EventHandlers<MergeEvents>
{
  execute({ connectedItems, spawn }: MergeEvents["execute"]) {
    const game = this.game;
    // Merge animation centers between drop sources AND spawn destinations.
    const positions = [
      ...connectedItems.map((i) => i.position).filter((p): p is Position => !!p),
      ...spawn.map((s) => s.tile.position),
    ];
    const center = centroidOf(positions);
    const duration = game.data.animations["merge:collapse"].duration;

    for (const item of connectedItems) {
      if (!item.position) continue;
      item.animation = {
        type: "merge:collapse",
        from: item.position,
        to: center,
        startTick: game.state.tick,
        endTick: game.state.tick + duration,
      };
    }

    game.schedule("merge:spawn", game.state.tick + duration, {
      connectedItemUids: connectedItems.map((item) => item.uid),
      spawn: spawn.map(({ itemId, tile }) => ({ itemId, tileKey: tile.key })),
      center,
    });
  }

  spawn({ connectedItems, spawn, center }: MergeEvents["spawn"]) {
    const game = this.game;
    // Remove all merged items
    for (const item of connectedItems) {
      game.state.items.delete(item.uid);
      if (item.position) {
        const tile = game.grid.tryAt(item.position);
        if (tile?.item === item) {
          tile.item = null;
        }
      }
    }

    const spawnedItems: Item[] = [];
    for (const { itemId, tile } of game.spawn.resolvePlanned(spawn)) {
      const newItem = game.item.spawn({
        itemId,
        tile,
        animation: { type: "merge:spread", from: center },
      });
      spawnedItems.push(newItem);
    }

    game.schedule("merge:complete", game.state.tick + game.data.animations["merge:spread"].duration, {
      spawnedItemUids: spawnedItems.map((item) => item.uid),
    });
  }

  complete({ spawnedItems }: MergeEvents["complete"]) {
    const game = this.game;
    for (const item of spawnedItems) {
      item.animation = undefined;
    }

    // Check for chain merges - if any spawned item can also merge via chain-enabled recipes.
    for (const item of spawnedItems) {
      if (!item.position) continue;
      const tile = game.grid.tryAt(item.position);
      if (!tile || tile.item !== item) continue;

      const avgX = Math.floor(
        spawnedItems.reduce((sum, i) => sum + (i.position?.x ?? 0), 0) / spawnedItems.length,
      );
      const avgY = Math.floor(
        spawnedItems.reduce((sum, i) => sum + (i.position?.y ?? 0), 0) / spawnedItems.length,
      );
      const center = { x: avgX, y: avgY };

      const merge = this.calculateDropMerge(tile, item, center, "auto")
        ?? this.calculateDropMerge(tile, item, center, "chain");
      if (merge && merge.spawn) {
        game.merge.execute({
          connectedItems: merge.connectedItems,
          spawn: merge.spawn,
        });
        break;
      }
    }

    game.merge.checkAll("auto");
  }

  checkAll(cause: mergeCause): boolean {
    const game = this.game;

    const tiles = Array.from(game.state.tiles.values()).sort((a, b) => {
      if (a.position.y !== b.position.y) return a.position.y - b.position.y;
      return a.position.x - b.position.x;
    });

    for (const tile of tiles) {
      const item = tile.item;
      if (!item || item.animation || !item.position) continue;
      if (!this.isGroupable(tile)) continue;
      const merge = this.calculateDropMerge(tile, item, null, cause);
      if (!merge?.spawn) continue;

      game.merge.execute({
        connectedItems: merge.connectedItems,
        spawn: merge.spawn,
      });
      return true;
    }

    return false;
  }

  settleBoard(): boolean {
    return this.checkAll("auto");
  }

  /** A tile that can participate in a merge group: not shaded, no animating item. */
  isGroupable(tile: Tile): boolean {
    if (tile.shade) return false;
    if (tile.item?.animation) return false;
    if (tile.item?.uid === this.game.state.dragging?.itemUid) return false;
    return true;
  }

  /** Tiles connected for a hypothetical drop of `item` on `target` (the merge cluster). */
  clusterFor(item: Item, target: Tile): Tile[] {
    const game = this.game;
    const source = item.position ? game.grid.tryAt(item.position) : null;
    const movedUid = source && source !== target ? item.uid : null;
    const flood = game.grid.scan({
      mode: "flood",
      from: target,
      valid: (t) => this.isValidPosition(t, item.id, target.key, movedUid),
      count: null,
      range: null,
    });
    return flood.tiles;
  }

  /** Compute a merge result for `dropItem` landing on `dropTile`, or null if no merge. */
  calculateDropMerge(
    dropTile: Tile,
    dropItem: Item | null,
    center: Position | PositionF | "drop" | null,
    cause: mergeCause,
  ): {
    dropTile: Tile;
    dropItem: Item;
    connectedItems: Item[];
    drops: Record<itemId, number>;
    spawn: { itemId: itemId; tile: Tile }[] | null;
  } | null {
    const game = this.game;
    const movedItemUid = dropItem?.uid ?? null;
    dropItem ??= dropTile.item ?? null;
    if (!dropItem) return null;
    if (dropTile.shade) return null;

    const flood = game.grid.scan({
      mode: "flood",
      from: dropTile,
      valid: (tile) => this.isValidPosition(tile, dropItem.id, dropTile.key, movedItemUid),
      count: null,
      range: null,
    });
    const connectedItems = unique([dropItem, ...flood.items.filter((i) => i.id === dropItem.id)]);
    const drops = calculateMergeDrops(connectedItems.length, dropItem.data, cause);
    if (!drops) return null;

    const spawnFrom = center === "drop"
      ? dropTile
      : game.grid.nearestTo(center ?? centroidOf(flood.tiles.map((t) => t.position)), flood.tiles);
    const assignment = game.spawn.assign({
      from: spawnFrom,
      drops,
      range: null,
      freed: flood.tiles,
    });
    const spawn = assignment.fits ? assignment.spawn : null;

    return { dropTile, dropItem, connectedItems, drops, spawn };
  }

  /** Whether `tile` is connected for a merge of `itemId` dropped on `dropTileKey`. */
  isValidPosition(
    tile: Tile,
    itemId: itemId,
    dropTileKey: tileKey | null,
    movedItemUid: instanceId | null,
  ): boolean {
    if (!this.isGroupable(tile)) return false;
    if (tile.key === dropTileKey) return true;
    if (!tile.item) return false;
    if (tile.item.uid === movedItemUid) return false;
    return tile.item.id === itemId;
  }
}

